import { v4 as uuidv4 } from 'uuid';

import { lib, enc } from 'crypto-js';
import {
  validateDigit,
  validateLowerCaseLetter,
  validateSpecialCharacter,
  validateUpperCaseLetter,
} from '../../validators';

import { Asymmetric } from '../cryptography/Asymmetric';

const NUMBERS = '0123456789';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

const SPECIAL_CHARACTERS = '!@#$%^&*()-+';

const CHARS_ALL = LETTERS + NUMBERS + SPECIAL_CHARACTERS;

export interface IKeyPair {
  privateKey?: string;
  publicKey: string;
}

export const generateAsymmetricKeys = async (): Promise<Required<IKeyPair>> => {
  const keys = await Asymmetric.generate();
  const {
    privateKey,
    publicKey,
  } = keys.serialize() as Required<IKeyPair>;
  return {
    publicKey,
    privateKey,
  };
};

export const generateSymmetricKey = (bits: number = 256) => lib
  .WordArray
  .random(bits / 8)
  .toString(enc.Hex);

export const generateGUID = () => uuidv4();

export const generateRandomString = (length: number = 12): string => {
  const charactersLength = CHARS_ALL.length;
  return Array
    .from(Array(length)
      .keys())
    .map(() => CHARS_ALL.charAt(Math.floor(Math.random() * charactersLength)))
    .join('');
};

interface ICheckCriteriaParams {
  includeNumbers: boolean;
  includeLetters: boolean;
  includeSpecialCharacters: boolean;
  password: string;
}

export function checkPasswordCriteria(params: ICheckCriteriaParams) {
  const {
    includeNumbers,
    includeLetters,
    includeSpecialCharacters,
    password,
  } = params;
  if (
    includeSpecialCharacters && !validateSpecialCharacter(password)
  ) return false;
  if (
    includeNumbers && !validateDigit(password)
  ) return false;
  if (
    includeLetters && !validateLowerCaseLetter(password) && !validateUpperCaseLetter(password)
  ) return false;
  return true;
}

export interface IGenerateRandomPassword {
  includeNumbers: boolean;
  includeLetters: boolean;
  includeSpecialCharacters: boolean;
  length: number;
}

export const generateRandomPassword = (params: IGenerateRandomPassword): string => {
  const {
    includeNumbers,
    includeSpecialCharacters,
    includeLetters,
    length,
  } = params;

  let charsToUse = '';

  if (includeNumbers) {
    charsToUse += NUMBERS;
  }
  if (includeSpecialCharacters) {
    charsToUse += SPECIAL_CHARACTERS;
  }
  if (includeLetters) {
    charsToUse += LETTERS;
  }

  let password = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    password = Array
      .from(Array(length)
        .keys())
      .map(() => charsToUse.charAt(Math.floor(Math.random() * charsToUse.length)))
      .join('');
    const isPasswordMatchCriteria = checkPasswordCriteria({
      includeLetters,
      password,
      includeNumbers,
      includeSpecialCharacters,
    });
    if (isPasswordMatchCriteria) {
      break;
    }
  }
  return password;
};
